import type { FormSheetConfig } from './form-config.types';
import type {
  CellStyle,
  ConditionalFormatRule,
  HorizontalAlign,
} from './report-style.types';

/**
 * Celda de fórmula viva: Excel recalcula `formula` (sin el `=`
 * inicial, sintaxis en-US) cuando el usuario edita la hoja, y
 * `result` es el valor cacheado que se pinta al abrir el archivo
 * sin recalcular. `result: null`/omitido = sin caché (el viewer
 * la muestra vacía hasta recalcular). Los totales de un reporte
 * deben viajar así: fórmula para Excel + el precálculo del
 * dominio como caché — nunca solo el número.
 */
export interface FormulaCellValue {
  readonly formula: string;
  readonly result?: number | string | boolean | Date | null;
}

export type CellValue =
  | string
  | number
  | boolean
  | Date
  | null
  | undefined
  | FormulaCellValue;

/** Type guard del boundary: separa fórmulas de escalares/Date. */
export function isFormulaCellValue(
  value: CellValue | unknown,
): value is FormulaCellValue {
  return (
    typeof value === 'object' &&
    value !== null &&
    typeof (value as FormulaCellValue).formula === 'string'
  );
}

export interface ColumnDefinition<T> {
  readonly key: keyof T & string;
  readonly header: string;
  readonly width?: number;
  readonly numFmt?: string;
  readonly align?: HorizontalAlign;
  /**
   * Columna auxiliar oculta en Excel (`<col hidden>`): sus celdas
   * se escriben igual — las fórmulas pueden referenciarlas, pero
   * el usuario no la ve salvo que la muestre a mano. Útil para
   * mandar insumos de fórmulas sin alterar el layout visible.
   */
  readonly hidden?: boolean;
  /**
   * Columna separadora entre tablas lado a lado: queda vacía y
   * sin estilo, en el header y en los datos (ignora `formatter`
   * y `cellStyle`), así cada tabla se lee como un bloque propio.
   */
  readonly spacer?: boolean;
  readonly formatter?: (row: T) => CellValue;
  readonly cellStyle?: (row: T) => Partial<CellStyle>;
}

/**
 * Tramo combinado de un renglón de pre-header, con valor y
 * estilo propios: varios por renglón permiten titular cada tabla
 * de una hoja con tablas lado a lado. Columnas 1-based e
 * inclusivas; `rowSpan` > 1 combina también hacia abajo.
 */
export interface PreHeaderSegment {
  readonly fromColumn: number;
  readonly toColumn: number;
  readonly value: CellValue;
  readonly rowSpan?: number;
  readonly style?: Partial<CellStyle>;
}

export interface PreHeaderRow {
  readonly values: ReadonlyArray<CellValue>;
  readonly merge?: string;
  readonly style?: Partial<CellStyle>;
  readonly height?: number;
  readonly segments?: ReadonlyArray<PreHeaderSegment>;
}

export interface SheetConfig<T> {
  readonly name: string;
  readonly columns: ReadonlyArray<ColumnDefinition<T>>;
  readonly rows: ReadonlyArray<T>;
  readonly autoFilter?: boolean;
  /**
   * Filas al final de `rows` (totales, renglón en blanco) que quedan
   * FUERA del rango del autoFilter: ordenar o filtrar no las mueve ni
   * las oculta.
   */
  readonly autoFilterFooterRows?: number;
  readonly freezeHeader?: boolean;
  readonly conditionalFormats?: ReadonlyArray<ConditionalFormatRule>;
  readonly headerStyleOverride?: Partial<CellStyle>;
  readonly preHeaderRows?: ReadonlyArray<PreHeaderRow>;
}

// Type-erased alias used at the workbook boundary, where each sheet
// may carry a different row shape T. Keeping the generic concrete at
// the construction site (caller declares SheetConfig<Venta>) preserves
// typing where it matters; the erasure only happens when collecting
// heterogeneous sheets into a single workbook.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export type AnySheetConfig = SheetConfig<any>;

// A workbook mixes tabular sheets (columns + rows) with free-form
// sheets (positioned cells); the engine dispatches via isFormSheet.
export type ReportSheetConfig = AnySheetConfig | FormSheetConfig;

export interface ReportConfig {
  readonly fileName: string;
  readonly author?: string;
  readonly title?: string;
  readonly sheets: ReadonlyArray<ReportSheetConfig>;
}
